import * as React from 'react';
import { Controller, type Control, type FieldValues, type UseFormSetValue } from 'react-hook-form';

import { DatePicker, type DatePickerProps } from '../composites/DatePicker';
import { FormField } from '../patterns/FormField';

import { rhfInlineError } from './rhfInlineError';
import type { Reaction, Effect } from './reactions';
import { useReactions } from './useReactions';
import { useEffects } from './useEffects';
import type { RhfLayoutProps } from './rhfLayout';
import { parseDateLocal } from '@packages/core';

function coerceToDate(value: Date | string | undefined): Date | undefined {
  if (value == null) return undefined;
  if (value instanceof Date) return value;
  if (typeof value === 'string' && value !== '') return parseDateLocal(value);
  return undefined;
}

export interface RhfDatePickerProps
  extends Omit<DatePickerProps, 'value' | 'onChange' | 'invalid' | 'name'>, RhfLayoutProps {
  control: Control<FieldValues>;
  name: string;
  label: string;
  hint?: string;
  required?: boolean;
  semanticId?: string;
  disabled?: boolean;
  reactions?: Reaction[];
  effects?: Effect[];
  setValue?: UseFormSetValue<FieldValues>;
}

export const RhfDatePicker = React.forwardRef<HTMLDivElement, RhfDatePickerProps>(
  function RhfDatePicker(
    {
      control,
      name,
      label,
      hint,
      required = false,
      semanticId,
      disabled = false,
      reactions,
      effects,
      setValue,
      controlWidth,
      ...rest
    },
    ref,
  ) {
    const overrides = useReactions(control, reactions);
    const { runEffects } = useEffects(effects, setValue);

    const finalDisabled = overrides.disabled ?? disabled;
    const finalRequired = (overrides.required as boolean) ?? required;
    const finalLabel = (overrides.label as string) ?? label;
    const finalMinDate = coerceToDate(overrides.minDate as Date | string | undefined) ?? rest.minDate;
    const finalMaxDate = coerceToDate(overrides.maxDate as Date | string | undefined) ?? rest.maxDate;

    if (overrides.visible === false) return null;

    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState, formState }) => {
          const errorText = rhfInlineError(fieldState, { isSubmitted: formState.isSubmitted });
          const dateValue =
            field.value instanceof Date
              ? field.value
              : field.value != null && field.value !== ''
                ? parseDateLocal(field.value as string | number)
                : null;

          return (
            <FormField
              ref={ref}
              label={finalLabel}
              hint={hint}
              required={finalRequired}
              semanticId={semanticId}
              disabled={finalDisabled}
              error={errorText}
              controlWidth={controlWidth}
            >
              <DatePicker
                name={name}
                value={dateValue}
                onChange={(date) => {
                  const formatted = date instanceof Date
                    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
                    : date ?? '';
                  field.onChange(formatted);
                  runEffects(formatted);
                }}
                onInputBlur={field.onBlur}
                invalid={Boolean(errorText)}
                disabled={finalDisabled}
                semanticId={semanticId}
                minDate={finalMinDate}
                maxDate={finalMaxDate}
                {...rest}
              />
            </FormField>
          );
        }}
      />
    );
  },
);

RhfDatePicker.displayName = 'RhfDatePicker';
